import React, { useEffect, useState } from 'react';
import { Calculator, Check, Coins, CornerUpLeft, FileSpreadsheet, Plus, Receipt, RotateCcw, Send, X } from 'lucide-react';
import * as hr from '../../api';
import { useHR } from '../../HRContext';
import Modal from '../../components/Modal';
import { MonthInput, Select } from '../../components/Controls';
import { download, fmtMoney, toCSV } from '../../format';
import { Chip, Kpi } from '../attendance/shared';
import { Empty, InnerTabs, MON_TONE, PAY_TONE, monthName, num, typeId } from './shared';

// Leave Monetization (Stephanet's leavemgmt/Monetization.jsx): converting unused leave days into salary payment while still at work.
// Register (all requests) · New Request (with the calculation) · Encashment Register (approved and posted to payroll).
// Approval runs in the LEAVE_MONETIZATION workflow; an approved request puts the gross on the payroll inputs register.

const VIEWS = [['register', 'Monetization Register', Receipt], ['new', 'New Request', Plus], ['encash', 'Encashment Register', FileSpreadsheet]];
const ACT = {
  approve: ['Approve', Check, 'lvm-ok', 'Approve & Post'], reject: ['Reject', X, 'att2-danger', 'Reject'],
  return: ['Return', CornerUpLeft, 'lvm-warn', 'Return'], resubmit: ['Resubmit', RotateCcw, '', 'Resubmit'],
};
const cap = s => s[0].toUpperCase() + s.slice(1);

export default function Monetization({ data, reload, tick }) {
  const [view, setView] = useState('register');
  const [acting, setActing] = useState(null);
  const list = view === 'encash' ? data.monetizations.filter(m => m.status === 'approved') : data.monetizations;
  const sum = k => list.reduce((s, m) => s + (m[k] || 0), 0);
  const exportCsv = () => download(`leave-${view === 'encash' ? 'encashment' : 'monetization'}-register.csv`, toCSV([
    { label: 'Request ID', get: m => m.reference }, { label: 'Employee', get: m => m.name }, { label: 'Department', get: m => m.department },
    { label: 'Leave Year', get: m => m.leave_year }, { label: 'Leave Type', get: m => m.leave_type }, { label: 'Balance Before', get: m => m.balance_before ?? '' },
    { label: 'Days Monetized', get: m => m.days }, { label: 'Daily Rate', get: m => m.daily_rate }, { label: 'Gross Amount', get: m => m.gross_amount },
    { label: 'Tax', get: m => m.tax_amount }, { label: 'Net Amount', get: m => m.net_amount }, { label: 'Payroll Month', get: m => m.payroll_month },
    { label: 'Status', get: m => m.status }, { label: 'Payment Status', get: m => m.payment_status },
  ], list));
  return (
    <>
      <InnerTabs items={VIEWS} active={view} onChange={setView} sub />
      {view === 'new' ? <NewMonetization data={data} onDone={() => { setView('register'); reload(); }} /> : (
        <>
          <div className="md-kpis mds-kpis lvm-kpis" key={`m${tick}${view}`}>
            <Kpi label="Requests" value={list.length} Ico={Receipt} tone="blue" />
            <Kpi label="Days Monetized" value={num(sum('days'))} Ico={Coins} tone="teal" i={1} />
            <Kpi label="Gross Amount" value={fmtMoney(sum('gross_amount'))} Ico={Coins} tone="green" i={2} />
            <Kpi label="Net Amount" value={fmtMoney(sum('net_amount'))} Ico={Coins} tone="purple" note={`after est. tax ${data.tax_rate}%`} i={3} />
          </div>
          <div className="mdr-card mdr-table-card">
            <div className="mds-table-head">
              <b>{view === 'encash' ? 'Leave Encashment Register' : 'Leave Monetization Register'}</b>
              <div className="att2-filters no-print">
                {data.can.export && <button className="mdr-btn" onClick={exportCsv} disabled={!list.length}><FileSpreadsheet size={13} /> Export</button>}
                {view === 'register' && (data.can.create || data.can.edit) && <button className="mds-run sm" onClick={() => setView('new')}><Plus size={13} /> New Monetization</button>}
              </div>
            </div>
            <div className="mdr-scroll">
              <table className="mdr-table mds-table">
                <thead><tr>{data.can.approve && view === 'register' && <th className="c no-print">Action</th>}<th>Request ID</th><th>Employee</th><th>Dept</th><th className="c">Leave Year</th><th>Leave Type</th>
                  <th className="c">Balance Before</th><th className="c">Days Monetized</th><th className="r">Daily Rate</th><th className="r">Gross Amount</th><th className="r">Tax</th>
                  <th className="r">Net Amount</th><th>Payroll Month</th><th>Status</th><th>Payment Status</th></tr></thead>
                <tbody key={tick}>
                  {list.map((m, i) => (
                    <tr key={m.id} className="mdr-row" style={{ animationDelay: `${Math.min(i, 20) * 15}ms` }}>
                      {data.can.approve && view === 'register' && <td className="c no-print nowrap">
                        {m.status === 'pending' ? <>
                          <button className="lvm-icon ok" title="Approve and post to payroll" onClick={() => setActing({ m, action: 'approve' })}><Check size={13} /></button>
                          <button className="lvm-icon bad" title="Reject" onClick={() => setActing({ m, action: 'reject' })}><X size={13} /></button>
                          <button className="lvm-icon" title="Return for correction" onClick={() => setActing({ m, action: 'return' })}><CornerUpLeft size={13} /></button>
                        </> : m.status === 'returned' ? <button className="lvm-icon ok" title="Resubmit" onClick={() => setActing({ m, action: 'resubmit' })}><RotateCcw size={13} /></button>
                          : <span className="mds-dash">—</span>}
                      </td>}
                      <td className="mdr-id">{m.reference}</td><td className="b nowrap">{m.name}</td><td>{m.department || '—'}</td><td className="c">{m.leave_year}</td>
                      <td>{String(m.leave_type).toUpperCase()}</td><td className="c">{num(m.balance_before)}</td><td className="c b">{num(m.days)}</td>
                      <td className="r">{fmtMoney(m.daily_rate)}</td><td className="r">{fmtMoney(m.gross_amount)}</td><td className="r rd">{fmtMoney(m.tax_amount)}</td>
                      <td className="r b g">{fmtMoney(m.net_amount)}</td><td className="nowrap">{monthName(m.payroll_month)}</td>
                      <td><Chip tone={MON_TONE[m.status]}>{cap(m.status)}</Chip>{m.comment && ['rejected', 'returned'].includes(m.status) && <small className="lvm-why">{m.comment}</small>}</td>
                      <td><Chip tone={PAY_TONE[m.payment_status]}>{m.payment_status}</Chip></td>
                    </tr>
                  ))}
                  {!list.length && <Empty cols={15}>{view === 'encash' ? 'No leave has been encashed yet.' : 'No monetization requests yet.'}</Empty>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      {acting && <MonetizationAction {...acting} onClose={() => setActing(null)} onDone={() => { setActing(null); reload(); }} />}
    </>
  );
}

function MonetizationAction({ m, action, onClose, onDone }) {
  const { notify } = useHR();
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [label, Ico, cls, button] = ACT[action];
  const needs = action === 'reject' || action === 'return';
  const run = () => {
    setBusy(true);
    hr.monetizationAction({ id: m.id, action, comment: comment || null })
      .then(r => { notify(`${m.reference}: ${r.status === 'approved' ? 'approved and posted to payroll' : r.status}`); onDone(); })
      .catch(e => notify(e.message, 'bad')).finally(() => setBusy(false));
  };
  return (
    <Modal className="mdl-modal" title={<><Ico size={18} /> {label} Monetization</>} onClose={() => !busy && onClose()}>
      <div className="mdl-modal-body">
        <p className="mdl-modal-lead">{action === 'approve'
          ? <>Approve <b>{m.reference}</b> for {m.name}: <b>{num(m.days)}</b> day(s) of {m.leave_type} leave, gross <b>{fmtMoney(m.gross_amount)}</b>. The days come off the balance and the amount is posted to the <b>{monthName(m.payroll_month)}</b> payroll as a one-off payment.</>
          : action === 'reject' ? <>Reject <b>{m.reference}</b> for {m.name}? Nothing is posted and the balance is unchanged.</>
            : action === 'return' ? <>Return <b>{m.reference}</b> for {m.name} to be checked and resubmitted.</>
              : <>Resubmit <b>{m.reference}</b> for approval.</>}</p>
        <label className="mdl-field">{needs ? 'Reason *' : 'Comment (optional)'}<textarea rows={2} value={comment} onChange={e => setComment(e.target.value)} /></label>
        <div className="mdh-form-foot"><button className="mdr-btn" disabled={busy} onClick={onClose}>Cancel</button>
          <button className={`mds-run ${cls}`} disabled={busy || (needs && !comment.trim())} onClick={run}><Ico size={14} /> {busy ? 'Saving…' : button}</button></div>
      </div>
    </Modal>
  );
}

function NewMonetization({ data, onDone }) {
  const { notify } = useHR();
  const year = Number(data.today.slice(0, 4));
  const [f, setF] = useState({ employee_id: '', leave_type: 'Annual', leave_year: year, days: '', rate_method: 'gross', fixed_rate: '',
    working_days: String(Math.round(data.working_days_per_month || 22)), payroll_month: data.today.slice(0, 7), reason: '' });
  const [calc, setCalc] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => { setF(x => ({ ...x, [k]: v })); setCalc(null); };
  const emp = data.employees.find(e => String(e.id) === String(f.employee_id));
  const monetizable = data.types.filter(t => t.has_balance && t.active !== false && t.paid !== false);
  const bal = emp && Number(f.leave_year) === data.year ? emp.balances[f.leave_type] : null;
  const salary = emp ? (f.rate_method === 'basic' ? emp.basic_salary : emp.gross_salary) : null;
  const body = () => ({ employee_id: f.employee_id, leave_type_id: typeId(data, f.leave_type), leave_year: Number(f.leave_year), days: Number(f.days),
    rate_method: f.rate_method, fixed_rate: f.rate_method === 'fixed' && f.fixed_rate !== '' ? Number(f.fixed_rate) : null,
    working_days: Number(f.working_days) || null, payroll_month: f.payroll_month, reason: f.reason || null });
  const calculate = () => hr.previewMonetization(body()).then(setCalc).catch(e => notify(e.message, 'bad'));
  useEffect(() => { setCalc(null); }, [f.employee_id]);
  const submit = e => {
    e.preventDefault();
    setBusy(true);
    hr.createMonetization(body()).then(r => { notify(`${r.reference}: monetization request submitted (net ${fmtMoney(r.net_amount)})`); onDone(); })
      .catch(err => notify(err.message, 'bad')).finally(() => setBusy(false));
  };
  return (
    <form className="mdr-card lvm-form" onSubmit={submit}>
      <div className="lvm-form-head">
        <div className="mds-card-title"><Coins size={17} /> New Leave Monetization Request</div>
        <p>Leave Monetization = Converting unused leave days into salary payment while still present at work.</p>
      </div>
      <section>
        <div className="lvm-grid">
          <label className="span2">Employee ID *
            <Select value={f.employee_id} onChange={e => set('employee_id', e.target.value)} required>
              <option value="">Select...</option>{data.employees.map(x => <option key={x.id} value={x.id}>{`${x.employee_no} — ${x.name}`}</option>)}
            </Select>
          </label>
          <label>Leave Type<Select value={f.leave_type} onChange={e => set('leave_type', e.target.value)}>{monetizable.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}</Select></label>
          <label>Leave Year<Select value={f.leave_year} onChange={e => set('leave_year', e.target.value)}>{[year - 2, year - 1, year].map(y => <option key={y} value={y}>{y}</option>)}</Select></label>
          <label>Days to Monetize *<input type="number" min="0.5" step="0.5" value={f.days} onChange={e => set('days', e.target.value)} required /></label>
          <label>Daily Rate Method<Select value={f.rate_method} onChange={e => set('rate_method', e.target.value)}>{Object.entries(data.rate_methods).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select></label>
          {f.rate_method === 'fixed'
            ? <label>Fixed Daily Rate (₦) *<input type="number" min="0" value={f.fixed_rate} onChange={e => set('fixed_rate', e.target.value)} required /></label>
            : <label>{f.rate_method === 'basic' ? 'Monthly Basic Salary (₦)' : 'Monthly Gross Salary (₦)'}<input value={salary === null ? '' : fmtMoney(salary)} readOnly placeholder="From the employee record" /></label>}
          <label>Working Days per Month<Select value={f.working_days} onChange={e => set('working_days', e.target.value)} disabled={f.rate_method === 'annual' || f.rate_method === 'fixed'}>
            {['20', '21', '22', '23', '26'].map(x => <option key={x} value={x}>{x}</option>)}</Select></label>
          <label>Payroll Month<MonthInput value={f.payroll_month} onChange={e => set('payroll_month', e.target.value)} required /></label>
        </div>
        {bal && <p className="lvm-hint">{emp.name} has <b>{num(bal.available)}</b> day(s) of {f.leave_type} leave available in {f.leave_year}.</p>}
        <button type="button" className="mdr-btn lvm-calc-btn" disabled={!emp || !Number(f.days)} onClick={calculate}><Calculator size={14} /> Calculate Monetization</button>
        {calc && (
          <div className="lvm-calc">
            <div><small>Daily Rate</small><b>{fmtMoney(calc.daily_rate)}</b><span>{data.rate_methods[f.rate_method]}</span></div>
            <div><small>Gross Amount</small><b>{fmtMoney(calc.gross_amount)}</b><span>{num(f.days)} day(s) × daily rate</span></div>
            <div><small>Tax ({calc.tax_rate}%)</small><b className="rd">{fmtMoney(calc.tax_amount)}</b><span>estimated withholding</span></div>
            <div className="ok"><small>Net Amount</small><b>{fmtMoney(calc.net_amount)}</b><span>gross − tax</span></div>
            <div className={calc.enough ? '' : 'bad'}><small>Balance</small><b>{num(calc.available)}</b><span>{calc.enough ? `after: ${num(calc.available - Number(f.days))}` : 'not enough days'}</span></div>
          </div>
        )}
        <label className="lvm-block">Reason<textarea rows={2} value={f.reason} onChange={e => set('reason', e.target.value)} placeholder="Why the leave is being monetized…" /></label>
      </section>
      <div className="mdh-form-foot lvm-form-foot">
        <button type="button" className="mdr-btn" onClick={onDone}>Cancel</button>
        <button className="mds-run" disabled={busy || !calc || !calc.enough}><Send size={14} /> {busy ? 'Submitting…' : 'Submit Request'}</button>
      </div>
    </form>
  );
}
